import React, { useEffect, useRef, useState } from 'react';

type Props = {
  as?: React.ElementType;
  delay?: number;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
} & Record<string, unknown>;

/**
 * Reveal-on-scroll wrapper mirroring the `data-reveal` behaviour from QSSN.dc.html:
 * elements fade + rise into place as they enter the viewport, with an optional
 * stagger delay. Respects `prefers-reduced-motion`.
 *
 * Robustness: reveals immediately if already in/above the viewport at mount, and
 * a safety timer force-reveals if the IntersectionObserver never fires (fast
 * scroll, anchor jump, browsers without IO) — so content can never stay hidden.
 */
const Reveal: React.FC<Props> = ({ as: Tag = 'div', delay = 0, className, style, children, ...rest }) => {
  const ref = useRef<any>(null);
  const [shown, setShown] = useState(false);
  const [reduce, setReduce] = useState(false);

  // Track prefers-reduced-motion, re-evaluating live if the user toggles it.
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setReduce(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Already in or above the viewport at mount → show without waiting for scroll.
    const vh = window.innerHeight || document.documentElement.clientHeight;
    if (el.getBoundingClientRect().top < vh * 0.92) {
      setShown(true);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShown(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -8% 0px' }
    );
    io.observe(el);

    // Safety net: never leave content stuck invisible if the observer is skipped.
    const safety = window.setTimeout(() => {
      setShown(true);
      io.disconnect();
    }, 2500);

    return () => {
      io.disconnect();
      window.clearTimeout(safety);
    };
  }, []);

  const motionStyle: React.CSSProperties = reduce
    ? {}
    : {
        opacity: shown ? 1 : 0,
        transform: shown ? 'none' : 'translateY(18px)',
        transition: 'opacity .7s cubic-bezier(.2,.6,.2,1), transform .7s cubic-bezier(.2,.6,.2,1)',
        transitionDelay: shown ? `${delay}ms` : '0ms',
      };

  return (
    <Tag ref={ref} className={className} style={{ ...style, ...motionStyle }} {...rest}>
      {children}
    </Tag>
  );
};

export default Reveal;
